/**
 * `motion`, mais dont l'état PRÉ-RENDU est l'état final.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LE BUG
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Framer Motion calcule le style initial d'un composant à partir de sa prop
 * `initial`. Au rendu serveur, il n'y a ni scroll ni animation : le HTML servi
 * porte donc littéralement l'état d'AVANT l'animation. Relevé dans la sortie de
 * production du 29 août 2026 :
 *
 *     <section style="opacity:0;transform:translateY(30px)">
 *
 * sur le hero, la bande de statistiques, les services, les zones, la FAQ et le
 * contact — c'est-à-dire sur toute la page. Un humain ne voit rien : le
 * JavaScript s'exécute et l'animation joue. Un agent qui filtre le contenu non
 * visible, lui, jette l'essentiel de la page. Et il a raison de le faire :
 * `opacity: 0` est la façon standard de masquer quelque chose.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LA CORRECTION
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Au pré-rendu, et là seulement, `initial` vaut `false`. Framer traite alors le
 * premier rendu comme un état déjà atteint :
 *
 *     initial={{opacity:0,y:30}} whileInView={{…}}   →  style="opacity:0;…"
 *     initial={false}            whileInView={{…}}   →  aucun style, visible
 *     initial={{opacity:0,x:50}} animate={{…}}       →  style="opacity:0;…"
 *     initial={false}            animate={{opacity:1,x:0}} → état final
 *
 * (Vérifié par `renderToString` sur framer-motion 11, pas déduit de la doc.)
 *
 * Côté navigateur, `PRERENDU` vaut faux et ce module réexporte le `motion`
 * d'origine, à l'identité près : aucune couche, aucun coût, aucune animation
 * modifiée. Ce qui change est le HTML servi, pas ce que voit le visiteur.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI PAS `MotionConfig isStatic`
 * ────────────────────────────────────────────────────────────────────────────
 *
 * C'était la première piste, et elle ne marche pas : `isStatic` désactive les
 * animations mais rend quand même l'état `initial`, donc `opacity:0`. Essayé,
 * mesuré, écarté.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LA RÈGLE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Aucun fichier de `src/` n'importe `motion` depuis `framer-motion`. Il l'importe
 * d'ici. Le contrôle de build échoue sur toute exception, et vérifie en outre
 * qu'aucune page pré-rendue ne contient `opacity:0` — parce que le prochain
 * composant animé ajouté au site ferait revenir le bug sans cela.
 *
 * Le reste de l'API — `AnimatePresence`, `useReducedMotion`, `useInView` — se
 * prend toujours dans `framer-motion` : rien de tout cela ne rend de style.
 */
import { createElement, forwardRef } from 'react';
import { motion as motionFramer } from 'framer-motion';

/** Vrai pendant le pré-rendu et le rendu SSR, faux dans un navigateur. */
export const PRERENDU = typeof window === 'undefined';

/**
 * Un composant enveloppé par balise, mémorisé.
 *
 * Sans ce cache, chaque accès à `motion.div` fabriquerait un nouveau type de
 * composant, que React considérerait comme un composant différent à chaque
 * rendu — remontage complet du sous-arbre à chaque fois.
 */
const enveloppes = new Map<string | symbol, unknown>();

type MotionSansInitial = Record<string, unknown>;

export const motion: typeof motionFramer = PRERENDU
  ? (new Proxy(motionFramer, {
      get(cible, propriete) {
        const original = Reflect.get(cible, propriete) as unknown;
        // `motion` expose aussi des choses qui ne sont pas des composants
        // (Symbol.toStringTag, etc.). On ne les enveloppe pas.
        if (typeof original !== 'function' && typeof original !== 'object') return original;
        if (original === null) return original;

        if (!enveloppes.has(propriete)) {
          const Enveloppe = forwardRef<unknown, MotionSansInitial>((props, ref) =>
            createElement(original as never, { ...props, initial: false, ref }),
          );
          Enveloppe.displayName = `motion.${String(propriete)}[pré-rendu]`;
          enveloppes.set(propriete, Enveloppe);
        }
        return enveloppes.get(propriete);
      },
    }) as typeof motionFramer)
  : motionFramer;
